<template>
  <div class="vip-pages-list">
    <div class="content-header">
      <button
        class="btn-back"
        title="Back to VIP CMS"
        @click="goBack"
      >
        <md-icon>arrow_back</md-icon>
      </button>
      <div class="content-header__title">
        <h3>VIP Pages for {{ websiteName }}</h3>
        <p>Open a page to edit its HTML, CSS and JavaScript per language.</p>
      </div>
      <div class="add-section" @click="addNewPage">
        <img src="@/assets/add-button.svg" class="add-button__new">
        <span>Add New Page</span>
      </div>
    </div>

    <div v-if="errorMessage" class="error-message">
      {{ errorMessage }}
    </div>

    <div class="c-table-wrapper">
      <div class="c-table__head">
        <div class="c-table__cell">
          Name
        </div>
        <div class="c-table__cell">
          Id
        </div>
        <div class="c-table__cell">
          Status
        </div>
        <div class="c-table__cell c-table__cell--actions" />
      </div>
      <div class="c-table__body">
        <template v-if="pages.length">
          <div v-for="page in pages" :key="page.id" class="c-table__row">
            <div class="c-table__cell">
              {{ page.name }}
            </div>
            <div class="c-table__cell">
              {{ page.id }}
            </div>
            <div class="c-table__cell">
              <span :class="`status status--${getStatus(page).toLowerCase()}`">
                {{ getStatus(page) }}
              </span>
            </div>
            <div class="c-table__cell c-table__cell--actions">
              <span class="c-table__btn-wrap">
                <generic-button
                  icon
                  transparent
                  text-inherit
                  @click="editPage(page.id)"
                >
                  <md-tooltip md-direction="top">
                    Edit page
                  </md-tooltip>
                  <img src="@/assets/edit-button.svg" alt="">
                </generic-button>
              </span>
            </div>
          </div>
        </template>
        <template v-else>
          <div class="c-table__row empty-row">
            <div class="c-table__cell empty-message">
              No VIP Pages found
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { vipService } from '@/services/vip';
import VipPageCreateModalComponent from '@/shared/vip-page-create-modal/VipPageCreateModalComponent';
import {onlinePermissionsMixin} from "@/mixins/onlinePermissionsMixin";
import GenericButton from "@/shared/buttons/GenericButton";


export default {
  name: 'VipPagesList',

  components: {
    GenericButton,
  },

  mixins: [onlinePermissionsMixin],

  data() {
    return {
      websiteId: null,
      websiteName: '',
      pages: [],
      errorMessage: '',
    };
  },

  created() {
    this.websiteId = this.$route.params.websiteId;
    if (!this.canReadWebsite(this.websiteId)) {
      this.$router.push({ name: 'on-line-vip' });
    }
    
    this.loadPages();
  },

  methods: {
    async loadPages() {
      try {
        const response = await vipService.getVipContent(this.websiteId);
        if (!response.data) {
          this.$router.push({ name: 'on-line-vip' });
          return;
        }
        this.websiteName = response.data.name || "";
        this.pages = response.data.data || [];
      } catch (error) {
        this.$router.push({ name: 'on-line-vip' });
      }
    },

    getStatus(page) {
      return page.status || '-';
    },

    editPage(pageId) {
      this.$router.push({
        name: 'on-line-vip-page-content',
        params: {
          vipPageId: pageId,
        },
      });
    },

    async addNewPage() {
      this.errorMessage = '';
      this.$modal.show(
        VipPageCreateModalComponent,
        {
          onAccept: this.createVipPage,
          onClose: () => {},
        },
        { height: 'auto', scrollable: false, width: "440px" }
      );
    },

    async createVipPage(pageName) {
      try {
        const newPageData = {
          websiteId: Number(this.websiteId),
          name: pageName,
        };

        const response = await vipService.createVipPage(newPageData);

        if (response && response.data && response.data.id) {
          this.$router.push({
            name: 'on-line-vip-page-content',
            params: {
              vipPageId: response.data.id,
            },
          });
        }
      } catch (error) {
        this.errorMessage = error.data?.message[0] || 'Error creating VIP page';
      }
    },

    goBack() {
      this.$router.push({ name: 'on-line-vip' });
    },
  },
};
</script>

<style lang="scss" scoped>
.vip-pages-list {
  font-family: var(--font-sans);

  // sticky header only needs to sit above the rows (it was 1000: above the modal overlay)
  .c-table__head {
    position: sticky;
    z-index: $zIndexStickyTop;
    top: 0;
  }

  .error-message {
    margin: 0 24px 16px;
    padding: 10px 14px;
    border: 1px solid var(--danger-border);
    border-radius: var(--radius-md);
    background-color: var(--danger-bg);
    color: var(--danger-fg);
    font-size: 13px;
  }

  .content-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 20px 24px;
    border-bottom: 1px solid var(--border-default);

    .btn-back {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      border: none;
      border-radius: var(--radius-md);
      background: none;
      color: var(--text-secondary);
      cursor: pointer;

      &:hover {
        background-color: var(--bg-hover);
        color: var(--text-primary);
      }

      .md-icon {
        color: inherit !important;
      }
    }

    h3 {
      margin: 0;
      font-size: 18px;
      font-weight: 600;
      color: var(--text-primary);
    }

    p {
      margin: 2px 0 0;
      font-size: 13px;
      color: var(--text-secondary);
    }
  }

  .content-header__title {
    flex: 1;
    min-width: 0;
  }

  // status as a badge
  .status {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 24px;
    padding: 0 10px;
    border: 1px solid var(--border-default);
    border-radius: var(--radius-full);
    background-color: var(--bg-subtle);
    color: var(--text-secondary);
    font-size: 12px;
    font-weight: 600;
    text-transform: capitalize;

    &:before {
      content: '';
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background-color: currentColor;
    }
  }

  .status--published {
    border-color: var(--success-border);
    background-color: var(--success-bg);
    color: var(--success-fg);
  }

  .status--draft {
    border-color: var(--info-border);
    background-color: var(--info-bg);
    color: var(--info-fg);
  }

  .status--inactive {
    color: var(--text-muted);
  }
}

.empty-message {
  text-align: center;
  color: var(--text-muted);
}
</style>
